/**
 * renewed-restored.data.ts — Content for the "Renewed & Restored" chapter
 * (CX-2727).
 *
 * Bio-circular (RE) and chemically-recycled (RP) feedstocks, mass-balance
 * certification (ISCC Plus), and the drop-in RE/RP grade portfolio. Ported from
 * the demo-v784 reference; static today, CMS-driven later (same `ChapterContent`
 * shape). Uses ordered `blocks` because the section order (ISCC callout + quote
 * above the grade table) differs from the fixed template order — see
 * chapter.types.ts `ChapterBlock`.
 */

import type { ChapterContent } from "../chapter.types";

const RENEWED_HERO_IMAGE =
  "https://mediahub.covestro.com/transform/90513457-0314-45b9-ba57-c3f5484651ae/Chemical-recycling";

const EVBOX_LIVO_IMAGE =
  "https://mediahub.covestro.com/transform/31bb4fff-980f-4002-afd0-0cd535ceeb75/EVBox-Livo";

export const renewedRestoredChapter: ChapterContent = {
  slug: "renewed-restored",
  title: "Renewed & Restored",
  eyebrow: "Sustainability Guide",
  heroImage: RENEWED_HERO_IMAGE,
  tags: [
    "Bio-Based",
    "Chemical Recycling",
    "Drop-In",
    "ISCC Plus",
    "Mass Balance",
  ],
  intro:
    "When the design requires the full color range, tight tolerances or specific optical properties, PCR grades may not be the right fit. That's where bio-circular and chemically recycled grades come in — and the key point is this: they behave exactly like standard grades in processing and application. Same tool. Same parameters. Same color range. Different origin.\n\nThe RE series (Renewed) replaces fossil feedstocks with bio-circular raw materials derived from biological waste and residues. Attribution is via mass balance — the same principle as a green electricity tariff. Selected Makrolon RE grades contain up to 72% bio-circular content by mass balance and achieve up to 50% less virgin fossil consumption compared to standard grades. Some selected grades reach climate neutrality. All RE grades are ISCC Plus certified. Covestro's production sites in Antwerp, Krefeld-Uerdingen, and Caojing are certified for RE production.\n\nThe RP series (Restored) uses chemically recycled post-consumer feedstocks. Chemical recycling breaks plastic waste down to its foundational monomers — enabling a truly circular material flow that mechanical recycling cannot achieve. The RP series carries a recycled-attributed share of up to 89%, also ISCC Plus certified. Both RE and RP are drop-in solutions. No tool changes. No process adjustments. No color limitations.",

  // Full-width alternating bands mirror the Figma section rhythm (nodes
  // 1658-11619 white / 1658-11728 #fafafa / 1658-12130 #f3f3f3), matching the
  // Reborn chapter. Band A holds the callout + quote + grade table (the order
  // that differs from the fixed template), band B the EVBox case study, band C
  // the material samples.
  blocks: [
    {
      kind: "band",
      surface: "white",
      blocks: [
        {
          kind: "note",
          note: {
            tone: "info",
            icon: "tag",
            text: "ISCC Plus (International Sustainability and Carbon Certification) is an internationally recognised standard covering the full supply chain from raw material to finished product. It ensures no double-counting of alternative raw materials across the value chain. Covestro's production sites are ISCC Plus certified for both RE and RP grades.",
          },
        },
        {
          kind: "quote",
          quote: {
            text: "Mass balance works like a green electricity tariff: the alternative raw material — bio-waste or chemically recycled feedstock — is fed into the production system, and the certified sustainable share is allocated to specific product batches, verified by independent auditors. The physical product is identical to a standard grade. The difference is in the origin of the raw material, not the material itself.",
          },
        },
        {
          kind: "suffixDecoder",
          decoder: {
            title: "Reading the grade name",
            text: "The number after the R tells you the recycled content level: R20 = 20% PCR, R30 = 30% PCR, R50 = 50% PCR, R65 = 65% PCR, R75 = 75% PCR, R90 = 90% PCR. The suffix RE marks a bio-circular (Renewed) grade — fossil feedstock is replaced by bio-circular raw materials attributed via mass balance. The suffix RP marks a chemically recycled (Restored) grade, using post-consumer feedstock broken back down to its monomers. The suffix CQ means the grade carries the Circular Intelligence designation. Both RE and RP are ISCC Plus certified and drop-in for their fossil analogues.",
          },
        },
        {
          kind: "subheading",
          text: "RE Grade Starting Portfolio (E&E and General Applications)",
        },
        {
          kind: "gradeTable",
          table: {
            columns: ["Grade", "Series", "Application", "Drop-in for"],
            rows: [
              [
                "Makrolon® 2407 RE",
                "RE",
                "General purpose, optical",
                "Makrolon 2407",
              ],
              ["Makrolon® 2807 RE", "RE", "General purpose", "Makrolon 2807"],
              [
                "Makrolon® FR6020 RE",
                "RE",
                "FR, E&E, EV charger",
                "Makrolon FR6020",
              ],
              [
                "Bayblend® FR3008 RE",
                "RE",
                "E&E, appliances",
                "Bayblend FR3008",
              ],
              [
                "Bayblend® FR3010 RE",
                "RE",
                "E&E, appliances, EV charger",
                "Bayblend FR3010",
              ],
              [
                "Bayblend® FR3040 R35 RE",
                "RE",
                "Thin wall FR",
                "Bayblend FR3040",
              ],
              ["Makroblend® RE", "RE", "PC/PET blends", "Makroblend standard"],
              ["Apec® RE", "RE", "High heat, optical", "Apec standard"],
              [
                "Makrolon® RP",
                "RP",
                "Full portfolio, chemically recycled",
                "Makrolon standard",
              ],
            ],
          },
        },
      ],
    },
    {
      kind: "band",
      surface: "gray",
      blocks: [
        {
          kind: "story",
          story: {
            title:
              "EVBox Livo — The World's First Wallbox Made with Makrolon® RE",
            image: EVBOX_LIVO_IMAGE,
            paragraphs: [
              "EVBox, one of the world's leading full-service providers for electric vehicle charging, used Makrolon® RE for the entire housing of their Livo wallbox — the world's first EV charger housing made from bio-circular polycarbonate.",
              "The grade is produced from bio-waste and residues attributed via mass balance, with a bio-circular content of more than 70%. Replacing 3.5 kg of standard Makrolon® with Makrolon® RE reduces the carbon footprint of a single charging station by approximately 10 kg CO₂ equivalents — verified by TÜV Rheinland. If all charging station housings worldwide made the same switch, approximately 450,000 metric tons less CO₂ equivalents could be emitted between 2022 and 2030.",
              "The switch required no retooling, no requalification, and no process changes. Makrolon® RE grades are listed on the UL Yellow Card alongside their fossil analogues — meaning the drop-in is immediate and certifiable.",
            ],
            quote: {
              text: "It is important to us to think about the entire life cycle of charging stations right from their development and design. These include circular materials as source materials, a modular design that facilitates repairs, and last but not least, the remanufacturing of components.",
              attribution:
                "Wijnand Diemer, Senior Director Product Management, EVBox Group",
            },
          },
        },
        {
          kind: "materialGroup",
          group: {
            primary: [
              {
                name: "Makrolon® 6487 RE",
                description:
                  "Housing / front cover · Up to 70.8% bio-circular · UL 94 V-0/1.5mm + 5VA/3.0mm · UV stabilized · f1 outdoor",
                link: {
                  variant: "view-in-imagio",
                  imagioUrl: "/search?grade=Makrolon%C2%AE+6487+RE&sort=-grade",
                },
              },
              {
                name: "Makrolon® FR6020 RE",
                description:
                  "Housing / front cover · Up to 65.4% bio-circular · UL 94 V-0/1.5mm + 5VA/3.0mm · UV stabilized · f1 outdoor",
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?grade=Makrolon%C2%AE+FR6020+RE&sort=-grade",
                },
              },
              {
                name: "Makrolon® 6485 GR",
                description:
                  "Housing / front cover — PCR alternative · 30% PCR · UL 94 V-0/1.5mm + 5VA/3.0mm",
                link: {
                  variant: "view-in-imagio",
                  imagioUrl: "/search?grade=Makrolon%C2%AE+6485+GR&sort=-grade",
                },
              },
            ],
            secondary: [
              {
                name: "Makrolon® 6557 RE",
                description:
                  "Design cover · Up to 71.5% bio-circular · UL 94 V-2 · UV stabilized · f1 outdoor",
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?grade=Makrolon%C2%AE+6557+RE*&sort=-grade",
                },
              },
              {
                name: "Makrolon® 2807 RE",
                description:
                  "Design cover · Up to 71.5% bio-circular · UL 94 V-2/1.5mm · UV stabilized · f1 outdoor",
                link: {
                  variant: "view-in-imagio",
                  imagioUrl: "/search?grade=Makrolon%C2%AE+2807+RE&sort=-grade",
                },
              },
            ],
          },
        },
      ],
    },
    // Material Samples — Figma renders a vertical list with status dots (name +
    // short description + "Check it now"). TODO(CX-2727): the demo reference has
    // no product-render image or full Color/Material/Opacity/Surface spec per
    // sample; populate `productImage` + spec fields from real sample data (or the
    // CMS) when available. Until then we render the short description + CTA.
    {
      kind: "band",
      surface: "light",
      blocks: [
        {
          kind: "samples",
          samples: [
            {
              name: "Makrolon® 2407 RE",
              description:
                "RE · General purpose, optical · drop-in for Makrolon 2407",
              link: { variant: "check-it-now" },
            },
            {
              name: "Makrolon® 2807 RE",
              description: "RE · General purpose · drop-in for Makrolon 2807",
              link: { variant: "check-it-now" },
            },
            {
              name: "Makrolon® FR6020 RE",
              description:
                "RE · FR, E&E, EV charger · drop-in for Makrolon FR6020",
              link: { variant: "check-it-now" },
            },
            {
              name: "Bayblend® FR3008 RE",
              description: "RE · E&E, appliances · drop-in for Bayblend FR3008",
              link: { variant: "check-it-now" },
            },
            {
              name: "Bayblend® FR3010 RE",
              description:
                "RE · E&E, appliances, EV charger · drop-in for Bayblend FR3010",
              link: { variant: "check-it-now" },
            },
            {
              name: "Bayblend® FR3040 R35 RE",
              description: "RE · Thin wall FR · drop-in for Bayblend FR3040",
              link: { variant: "check-it-now" },
            },
            {
              name: "Makroblend® RE",
              description:
                "RE · PC/PET blends · drop-in for Makroblend standard",
              link: { variant: "check-it-now" },
            },
            {
              name: "Apec® RE",
              description:
                "RE · High heat, optical · drop-in for Apec standard",
              link: { variant: "check-it-now" },
            },
            {
              name: "Makrolon® RP",
              description:
                "RP · Full portfolio, chemically recycled · drop-in for Makrolon standard",
              link: { variant: "check-it-now" },
            },
          ],
        },
      ],
    },
  ],
};
